import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, mensagemDoErro } from '../api';
import {
  Aviso, Campo as CampoTexto, Carregando, Dialogo, ErroDaApi, Escolha,
  Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';
import { useSessao } from '../sessao';
import { Diarios } from './Diarios';
import { Instrumentos } from './Instrumentos';
import {
  alterarEquipe, criarEquipe, criarVeiculo, encerrarMembro, incluirMembro,
  situacaoDoVeiculo, verEquipe, verEquipes, verVeiculos,
  type Equipe, type EquipeMembro, type Veiculo,
} from '../api/campo';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';

/**
 * Campo — diários, equipes e veículos.
 *
 * ── Por que numa tela só ────────────────────────────────────────────
 *
 * Porque são o mesmo assunto para quem os usa: quem coordena campo olha
 * o que as equipes fizeram, e quem monta a semana escolhe uma equipe E um
 * veículo, na mesma conversa. E porque o menu lateral tem teto —
 * dezoito itens é o que cabe num tablet em pé sem rolar, e esse teto não
 * é estética (teste `33.1`). Três itens separados custariam três linhas
 * do menu para responder a uma pergunta só.
 *
 * A ordem das abas é a ordem do uso: o diário é diário, os cadastros se
 * mexem uma vez por mês.
 *
 * ── O membro se ENCERRA, não se remove ──────────────────────────────
 *
 * Não há botão de remover, e a falta dele é a funcionalidade. Apagar a
 * linha faria o diário de campo de março passar a dizer que quem estava
 * lá é quem está hoje — e a restrição 25 chama isso de sumiço, não de
 * correção.
 */

const TOM_EQUIPE: Record<string, Tom> = {
  ativa: 'ok', inativa: 'neutro', bloqueada: 'critico',
};

const TOM_VEICULO: Record<string, Tom> = {
  disponivel: 'ok', em_uso: 'neutro', em_manutencao: 'atencao',
  bloqueado: 'critico', inativo: 'neutro',
};

const ROTULO: Record<string, string> = {
  ativa: 'Ativa', inativa: 'Inativa', bloqueada: 'Bloqueada',
  campo: 'Campo', laboratorio: 'Laboratório', mista: 'Mista',
  disponivel: 'Disponível', em_uso: 'Em uso', em_manutencao: 'Em manutenção',
  bloqueado: 'Bloqueado', inativo: 'Inativo',
  caminhonete: 'Caminhonete', caminhao: 'Caminhão', van: 'Van',
  carro: 'Carro', utilitario: 'Utilitário', reboque: 'Reboque',
  sonda_montada: 'Sonda montada', outro: 'Outro',
  encarregado: 'Encarregado', sondador: 'Sondador', operador: 'Operador',
  auxiliar: 'Auxiliar', motorista: 'Motorista', tecnico: 'Técnico',
  responsavel_tecnico: 'Resp. técnico',
};

const rotulo = (v: string) => ROTULO[v] ?? v;

interface Funcionario { id: string; nome: string; cargo: string | null }

export function CampoCadastros() {
  const [aba, setAba] =
    useState<'diarios' | 'instrumentos' | 'equipes' | 'veiculos'>('diarios');

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <h1>Campo</h1>
          <p className="sub">
            O que as equipes fizeram, quem vai e com o quê
          </p>
        </div>
        <AbrirOsNoCampo />
      </div>

      <div className="abas" role="tablist">
        <button
          type="button" role="tab" aria-selected={aba === 'diarios'}
          className={aba === 'diarios' ? 'ativa' : undefined}
          onClick={() => setAba('diarios')}
        >
          Diários
        </button>
        <button
          type="button" role="tab" aria-selected={aba === 'instrumentos'}
          className={aba === 'instrumentos' ? 'ativa' : undefined}
          onClick={() => setAba('instrumentos')}
        >
          Instrumentação
        </button>
        <button
          type="button" role="tab" aria-selected={aba === 'equipes'}
          className={aba === 'equipes' ? 'ativa' : undefined}
          onClick={() => setAba('equipes')}
        >
          Equipes
        </button>
        <button
          type="button" role="tab" aria-selected={aba === 'veiculos'}
          className={aba === 'veiculos' ? 'ativa' : undefined}
          onClick={() => setAba('veiculos')}
        >
          Veículos
        </button>
      </div>

      {aba === 'diarios' ? <Diarios />
        : aba === 'instrumentos' ? <Instrumentos />
          : aba === 'equipes' ? <Equipes /> : <Veiculos />}
    </div>
  );
}

// ── a porta do sondador para a OS ────────────────────────────────────

/** O que a lista de OS devolve e o bloco usa. */
interface OsAchada { id: string; numero: string; cliente: string | null; obra_nome: string | null }

/** Quantas OS o bloco lista quando o número casa com mais de uma. */
const ATE = 5;

/**
 * «Abrir uma OS no Campo» (H1, onda 295).
 *
 * Até a H1 o diário de uma OS nascia na ficha da OS: «Campo · furos e
 * diários», no menu «⋯» do cabeçalho. A ficha é do Hub e do LabManager, e o
 * sondador (Operador de campo, D6) não alcança nenhum dos dois — sem esta
 * porta, ele entraria no Campo e não teria por onde chegar à OS em que vai
 * trabalhar. Ele digita o número que está na ordem de serviço impressa: uma
 * OS vai direto ao Campo dela (`/os/:id/campo`, onde estão os furos e o
 * «Abrir diário»); várias viram até cinco links, para ele escolher; nenhuma
 * diz isso, em vez de uma lista vazia que parece tela travada.
 *
 * A busca é a da lista de OS (`GET /os?busca=`), que só pede o «Ver» da OS
 * — e esse o sondador tem.
 */
function AbrirOsNoCampo() {
  const [numero, setNumero] = useState('');
  const [achadas, setAchadas] = useState<OsAchada[] | null>(null);
  const [total, setTotal] = useState(0);
  const [erro, setErro] = useState<unknown>(null);
  const [buscando, setBuscando] = useState(false);
  const navegar = useNavigate();

  const abrir = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const termo = numero.trim();
    if (!termo) return;
    setBuscando(true); setErro(null); setAchadas(null);
    try {
      const r = await api<OsAchada[]>(`/os?busca=${encodeURIComponent(termo)}`);
      if (r.length === 1) { navegar(`/os/${r[0].id}/campo`); return; }
      setTotal(r.length);
      setAchadas(r.slice(0, ATE));
    } catch (e) {
      setErro(e);
    } finally {
      setBuscando(false);
    }
  };

  return (
    <form className="abrir-os-campo" onSubmit={(ev) => { void abrir(ev); }}
      aria-labelledby="abrir-os-campo-titulo">
      <h2 id="abrir-os-campo-titulo">Abrir uma OS no Campo</h2>
      <div className="abrir-os-campo-linha">
        <CampoTexto rotulo="Nº da OS" value={numero} autoComplete="off"
          onChange={(e) => { setNumero(e.target.value); setAchadas(null); }} />
        <button type="submit" className="principal" disabled={buscando || !numero.trim()}>
          {buscando ? 'Abrindo…' : 'Abrir'}
        </button>
      </div>
      {erro ? <ErroDaApi erro={erro} /> : null}
      {achadas && achadas.length === 0 && (
        <p className="sub" role="status">Nenhuma OS com esse número.</p>
      )}
      {achadas && achadas.length > 0 && (
        <>
          <ul className="abrir-os-campo-lista">
            {achadas.map((o) => (
              <li key={o.id}>
                <Link to={`/os/${o.id}/campo`}>
                  {[o.numero, o.cliente, o.obra_nome].filter(Boolean).join(' · ')}
                </Link>
              </li>
            ))}
          </ul>
          {total > ATE && (
            <p className="sub">
              {ATE} de {total}. Digite mais do número para achar a sua.
            </p>
          )}
        </>
      )}
    </form>
  );
}

// ── equipes ──────────────────────────────────────────────────────────

const hoje = (e: Equipe) => (e.membros_hoje > 0
  ? `${e.membros_hoje} pessoa(s)` : null);

/** As colunas das equipes de campo (onda 219). */
function colunasDaEquipe(abrir: (id: string) => void): ColunaComFicha<Equipe>[] {
  return [
    {
      chave: 'equipe',
      cabeca: <th scope="col">Equipe</th>,
      celula: (e) => (
        <th scope="row">
          <button type="button" className="ligacao" onClick={() => abrir(e.id)}>
            <strong>{e.nome}</strong>
          </button>
          <span className="sub">{e.codigo}</span>
          {e.base && <span className="sub">{e.base}</span>}
        </th>
      ),
    },
    {
      chave: 'tipo', detalhe: true,
      cabeca: <th scope="col" className="col-opcional">Tipo</th>,
      celula: (e) => <td className="col-opcional">{rotulo(e.tipo)}</td>,
    },
    {
      chave: 'responsavel', detalhe: true,
      cabeca: <th scope="col">Responsável</th>,
      celula: (e) => <td>{e.responsavel_nome ?? <span className="nada">—</span>}</td>,
    },
    {
      chave: 'hoje',
      cabeca: <th scope="col">Hoje</th>,
      celula: (e) => <td>{hoje(e) ?? <span className="nada">ninguém</span>}</td>,
    },
    {
      chave: 'situacao',
      cabeca: <th scope="col">Situação</th>,
      celula: (e) => (
        <td><Selo tom={TOM_EQUIPE[e.situacao] ?? 'neutro'}>{rotulo(e.situacao)}</Selo></td>
      ),
    },
  ];
}

/** A ficha da equipe ao lado da lista; as pessoas continuam na janela da equipe. */
function fichaDaEquipe(e: Equipe, abrir: (id: string) => void): FichaDaLinha {
  return {
    rotulo: 'Equipe de campo',
    titulo: e.nome,
    sub: <span className="mono">{e.codigo}</span>,
    corpo: (
      <>
        <div className="tf-selos">
          <Selo tom={TOM_EQUIPE[e.situacao] ?? 'neutro'}>{rotulo(e.situacao)}</Selo>
        </div>
        <CamposDaFicha itens={[
          ['Tipo', rotulo(e.tipo)],
          ['Responsável', e.responsavel_nome ?? <span className="nada">não definido</span>],
          ['Base', e.base],
          ['Hoje', hoje(e) ?? <span className="nada">ninguém</span>],
          ['Observações', e.observacoes],
        ]} />
      </>
    ),
    acoes: (
      <button type="button" className="botao" onClick={() => abrir(e.id)}>
        Abrir equipe
      </button>
    ),
  };
}

function Equipes() {
  const [linhas, setLinhas] = useState<Equipe[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [nova, setNova] = useState(false);
  const [aberta, setAberta] = useState<string | null>(null);
  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try { setLinhas(await verEquipes()); } catch (e) { setErro(e); }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Equipes</h2>
        <span className="sub">
          {linhas == null ? 'carregando'
            : `${linhas.length} equipe${linhas.length === 1 ? '' : 's'}`}
        </span>
        {pode('cadastro.editar') && (
          <button type="button" className="botao" onClick={() => setNova(true)}>
            Nova equipe
          </button>
        )}
      </div>

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : linhas.length === 0 ? (
          <Vazio>
            Nenhuma equipe cadastrada. A equipe dura mais que a ordem de
            serviço — é por ela que o §16 mede produtividade e o §8
            programa a semana.
          </Vazio>
        ) : (
          /*
           * As equipes numa tabela com ficha (onda 219): a equipe, quem
           * está nela hoje e a situação ficam; tipo e responsável são
           * detalhe e, com a ficha aberta, moram nela. O nome continua
           * abrindo a equipe (as pessoas e os períodos de cada uma).
           */
          <TabelaComFicha
            linhas={linhas}
            chave={(e) => e.id}
            nome={(e) => `Equipe ${e.nome}`}
            rotulo="Equipes"
            colunas={colunasDaEquipe(setAberta)}
            ficha={(e) => fichaDaEquipe(e, setAberta)}
          />
        )}

      {nova && (
        <FormEquipe
          aoFechar={() => setNova(false)}
          aoPronto={() => { setNova(false); void carregar(); }}
        />
      )}
      {aberta && (
        <FichaEquipe
          id={aberta}
          aoFechar={() => { setAberta(null); void carregar(); }}
        />
      )}
    </div>
  );
}

function FormEquipe({ aoFechar, aoPronto }: {
  aoFechar(): void; aoPronto(): void;
}) {
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('campo');
  const [base, setBase] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      await criarEquipe({
        codigo: codigo.trim().toUpperCase(), nome: nome.trim(), tipo,
        base: base.trim() || undefined,
      });
      aoPronto();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Nova equipe" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <CampoTexto
        rotulo="Código" value={codigo}
        dica="Curto e em maiúsculas, como EQ-01. É como a casa fala da equipe."
        onChange={(ev) => setCodigo(ev.target.value.toUpperCase())}
      />
      <CampoTexto
        rotulo="Nome" value={nome}
        onChange={(ev) => setNome(ev.target.value)}
      />
      <Escolha
        rotulo="Tipo" valor={tipo} limpavel={false}
        aoEscolher={(v) => setTipo(v ?? 'campo')}
        opcoes={[
          { valor: 'campo', texto: 'Campo' },
          { valor: 'laboratorio', texto: 'Laboratório' },
          { valor: 'mista', texto: 'Mista' },
        ]}
      />
      <CampoTexto
        rotulo="Base" value={base} dica="De onde ela sai — a sede, um canteiro."
        onChange={(ev) => setBase(ev.target.value)}
      />
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button
          type="button" className="principal"
          disabled={ocupado || codigo.trim().length < 2 || nome.trim().length < 3}
          onClick={() => { void gravar(); }}
        >
          {ocupado ? 'Gravando…' : 'Cadastrar'}
        </button>
      </div>
    </Dialogo>
  );
}

function FichaEquipe({ id, aoFechar }: { id: string; aoFechar(): void }) {
  const [d, setD] = useState<{ equipe: Equipe; membros: EquipeMembro[] } | null>(
    null);
  const [gente, setGente] = useState<Funcionario[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [quem, setQuem] = useState('');
  const [papel, setPapel] = useState('auxiliar');
  const [encerrando, setEncerrando] = useState<EquipeMembro | null>(null);
  const [motivo, setMotivo] = useState('');
  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    try { setD(await verEquipe(id)); } catch (e) { setErro(mensagemDoErro(e)); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);
  useEffect(() => {
    api<Funcionario[]>('/funcionarios').then(setGente).catch(() => setGente([]));
  }, []);

  const incluir = async () => {
    if (!quem) return;
    setOcupado(true); setErro(null);
    try {
      setD(await incluirMembro(id, { funcionarioId: quem, papel }));
      setQuem('');
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  const encerrar = async () => {
    if (!encerrando) return;
    setOcupado(true); setErro(null);
    try {
      setD(await encerrarMembro(id, encerrando.id, {
        fim: new Date().toLocaleDateString('en-CA'), motivo,
      }));
      setEncerrando(null); setMotivo('');
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  const mudarSituacao = async (situacao: string) => {
    setOcupado(true); setErro(null);
    try {
      await alterarEquipe(id, { situacao, motivo: `Situação: ${situacao}` });
      await carregar();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo
      aberto
      titulo={d ? `${d.equipe.codigo} · ${d.equipe.nome}` : 'Equipe'}
      aoFechar={aoFechar}
    >
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {!d ? <Carregando /> : (
        <>
          <p className="sub">
            {rotulo(d.equipe.tipo)}
            {d.equipe.base ? ` · ${d.equipe.base}` : ''}
            {d.equipe.responsavel_nome ? ` · ${d.equipe.responsavel_nome}` : ''}
          </p>

          {pode('cadastro.editar') && (
            <div className="linha" style={{ marginBottom: '.75rem' }}>
              <Escolha
                rotulo="Incluir" valor={quem}
                aoEscolher={(v) => setQuem(v ?? '')}
                opcoes={gente.map((g) => ({
                  valor: g.id, texto: g.nome, detalhe: g.cargo ?? undefined }))}
              />
              <Escolha
                rotulo="Papel" valor={papel} limpavel={false}
                aoEscolher={(v) => setPapel(v ?? 'auxiliar')}
                opcoes={['encarregado', 'sondador', 'operador', 'auxiliar',
                  'motorista', 'tecnico', 'responsavel_tecnico'].map((p) => ({
                  valor: p, texto: rotulo(p),
                }))}
              />
              <button
                type="button" disabled={ocupado || !quem}
                onClick={() => { void incluir(); }}
              >
                Incluir
              </button>
            </div>
          )}

          {d.membros.length === 0 ? (
            <Vazio>Ninguém nesta equipe ainda.</Vazio>
          ) : (
            <div className="rola">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Pessoa</th>
                    <th scope="col">Papel</th>
                    <th scope="col">Período</th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {d.membros.map((m) => (
                    <tr key={m.id} className={m.vigente ? undefined : 'desc'}>
                      <th scope="row">{m.funcionario_nome}</th>
                      <td>{rotulo(m.papel)}</td>
                      <td>
                        {m.inicio}
                        {m.fim ? ` até ${m.fim}` : ' · em curso'}
                      </td>
                      <td>
                        {m.vigente && pode('cadastro.editar') && (
                          <button
                            type="button" className="ligacao"
                            onClick={() => { setEncerrando(m); setMotivo(''); }}
                          >
                            encerrar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="sub">
            A participação se <b>encerra</b>, não se apaga: o diário de
            campo de março continua tendo de dizer quem estava lá em março.
          </p>

          {pode('cadastro.editar') && (
            <div className="acoes">
              {d.equipe.situacao === 'ativa' ? (
                <button
                  type="button" disabled={ocupado}
                  onClick={() => { void mudarSituacao('inativa'); }}
                >
                  Inativar
                </button>
              ) : (
                <button
                  type="button" disabled={ocupado}
                  onClick={() => { void mudarSituacao('ativa'); }}
                >
                  Reativar
                </button>
              )}
            </div>
          )}
        </>
      )}

      {encerrando && (
        <Dialogo
          aberto titulo="Encerrar participação"
          aoFechar={() => setEncerrando(null)}
        >
          <p className="sub">
            {encerrando.funcionario_nome} sai da equipe a partir de hoje. O
            histórico fica.
          </p>
          <label className="campo">
            <span className="rotulo">Por quê?</span>
            <textarea
              rows={3} value={motivo}
              onChange={(ev) => setMotivo(ev.target.value)}
              placeholder="mudou de equipe, saiu da empresa, férias longas…"
            />
          </label>
          <div className="acoes">
            <button type="button" onClick={() => setEncerrando(null)}>
              Cancelar
            </button>
            <button
              type="button" className="principal"
              disabled={ocupado || motivo.trim().length < 5}
              onClick={() => { void encerrar(); }}
            >
              {ocupado ? 'Gravando…' : 'Encerrar'}
            </button>
          </div>
        </Dialogo>
      )}
    </Dialogo>
  );
}

// ── veículos ─────────────────────────────────────────────────────────

function Veiculos() {
  const [linhas, setLinhas] = useState<Veiculo[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [novo, setNovo] = useState(false);
  const [mudando, setMudando] = useState<Veiculo | null>(null);
  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try { setLinhas(await verVeiculos()); } catch (e) { setErro(e); }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Veículos</h2>
        <span className="sub">
          {linhas == null ? 'carregando'
            : `${linhas.length} veículo${linhas.length === 1 ? '' : 's'}`}
        </span>
        {pode('cadastro.editar') && (
          <button type="button" className="botao" onClick={() => setNovo(true)}>
            Novo veículo
          </button>
        )}
      </div>

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : linhas.length === 0 ? (
          <Vazio>
            Nenhum veículo cadastrado. Ele não é equipamento — equipamento
            é o que mede, e o que mede tem calibração.
          </Vazio>
        ) : (
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">Placa</th>
                  <th scope="col">Tipo</th>
                  <th scope="col" className="col-opcional">Modelo</th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((v) => (
                  <tr key={v.id}>
                    <th scope="row">
                      <span className="mono">{v.placa}</span>
                      {v.descricao && <span className="sub">{v.descricao}</span>}
                    </th>
                    <td>{rotulo(v.tipo)}</td>
                    <td className="col-opcional">
                      {v.modelo ?? <span className="nada">—</span>}
                      {v.ano ? <span className="sub">{v.ano}</span> : null}
                    </td>
                    <td>
                      <Selo tom={TOM_VEICULO[v.situacao] ?? 'neutro'}>
                        {rotulo(v.situacao)}
                      </Selo>
                      {pode('cadastro.editar') && (
                        <button
                          type="button" className="ligacao"
                          onClick={() => setMudando(v)}
                        >
                          mudar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {novo && (
        <FormVeiculo
          aoFechar={() => setNovo(false)}
          aoPronto={() => { setNovo(false); void carregar(); }}
        />
      )}
      {mudando && (
        <DialogoSituacao
          veiculo={mudando}
          aoFechar={() => setMudando(null)}
          aoPronto={() => { setMudando(null); void carregar(); }}
        />
      )}
    </div>
  );
}

function FormVeiculo({ aoFechar, aoPronto }: {
  aoFechar(): void; aoPronto(): void;
}) {
  const [placa, setPlaca] = useState('');
  const [tipo, setTipo] = useState('caminhonete');
  const [modelo, setModelo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      await criarVeiculo({ placa, tipo, modelo: modelo.trim() || undefined });
      aoPronto();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Novo veículo" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <CampoTexto
        rotulo="Placa" value={placa}
        dica="Sem hífen. ABC1234 ou ABC1D23 — a placa é a identidade do veículo."
        onChange={(ev) => setPlaca(
          ev.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
      />
      <Escolha
        rotulo="Tipo" valor={tipo} limpavel={false}
        aoEscolher={(v) => setTipo(v ?? 'caminhonete')}
        opcoes={['caminhonete', 'caminhao', 'van', 'carro', 'utilitario',
          'reboque', 'sonda_montada', 'outro'].map((t) => ({
          valor: t, texto: rotulo(t),
        }))}
      />
      <CampoTexto
        rotulo="Modelo" value={modelo}
        onChange={(ev) => setModelo(ev.target.value)}
      />
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button
          type="button" className="principal"
          disabled={ocupado || placa.length < 7}
          onClick={() => { void gravar(); }}
        >
          {ocupado ? 'Gravando…' : 'Cadastrar'}
        </button>
      </div>
    </Dialogo>
  );
}

function DialogoSituacao({ veiculo, aoFechar, aoPronto }: {
  veiculo: Veiculo; aoFechar(): void; aoPronto(): void;
}) {
  const [situacao, setSituacao] = useState(veiculo.situacao);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      await situacaoDoVeiculo(veiculo.id, { situacao, motivo });
      aoPronto();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo={`Situação de ${veiculo.placa}`} aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <Escolha
        rotulo="Situação" valor={situacao} limpavel={false}
        aoEscolher={(v) => setSituacao(v ?? veiculo.situacao)}
        opcoes={['disponivel', 'em_uso', 'em_manutencao', 'bloqueado',
          'inativo'].map((s) => ({ valor: s, texto: rotulo(s) }))}
      />
      <label className="campo">
        <span className="rotulo">Por quê?</span>
        <textarea
          rows={3} value={motivo}
          onChange={(ev) => setMotivo(ev.target.value)}
          placeholder="revisão dos 40 mil, pneu, saiu para Itajaí…"
        />
      </label>
      <p className="sub">
        Quem monta o cronograma da semana que vem vai ler isto — um veículo
        que some da programação sem explicação não tem a quem perguntar.
      </p>
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button
          type="button" className="principal"
          disabled={ocupado || motivo.trim().length < 5}
          onClick={() => { void gravar(); }}
        >
          {ocupado ? 'Gravando…' : 'Gravar'}
        </button>
      </div>
    </Dialogo>
  );
}
